import { Box, Flex, Text } from "@chakra-ui/react";
import {
  buildPhaseDateTimeRangeViewModel,
  type PhaseCalendarNeighborBounds,
  type PhaseDateTimeRangeInput,
} from "./phaseDateTimeRangeModel";
import { PhaseRangeField, phaseRangeFieldFormats } from "./PhaseRangeField";

export type { PhaseCalendarNeighborBounds };

export type BudgetPhaseMetaDateTimeRangeProps = PhaseDateTimeRangeInput;

/**
 * Low-level range shell. Prefer {@link CampaignRunRangePicker} or
 * {@link ScheduledPhaseRangePicker} at call sites.
 *
 * Rendering is driven by {@link buildPhaseDateTimeRangeViewModel}. Requires a parent
 * {@link LocalizationProvider}. Do not control the picker's `open` prop from React state.
 */
export default function BudgetPhaseMetaDateTimeRange(
  props: BudgetPhaseMetaDateTimeRangeProps
) {
  const view = buildPhaseDateTimeRangeViewModel(props);

  const invalidOutline = (side: "start" | "end") =>
    view.invalidTimeField === side
      ? {
          outline: "2px solid",
          outlineColor: "red.400",
          outlineOffset: "2px",
          borderRadius: "md",
        }
      : {};

  const pickerChrome = {
    timezone: view.timezone,
    dateTimeFormat: phaseRangeFieldFormats.dateTime,
    dateFormat: phaseRangeFieldFormats.date,
    timeSteps: view.dateTimePicker.timeSteps,
    closeOnSelect: view.dateTimePicker.closeOnSelect,
    openIconAtStart: view.pickerOpenIconAtStart,
  };

  return (
    <Box
      w="full"
      minW={0}
      display="flex"
      alignItems="center"
      borderWidth="1px"
      borderColor="#E2E2E2"
      borderRadius="md"
      bg="white"
      boxShadow="0px 2px 6px rgba(0, 0, 0, 0.05)"
      px={3}
      py={2.5}
    >
      <Flex w="full" align="center" justify="flex-start" gap={2} minH="28px">
        <Flex
          minW={0}
          w="full"
          align="center"
          justify="flex-start"
          gap={2}
          flexWrap={{ base: "wrap", sm: "nowrap" }}
          rowGap={2}
        >
          <Box
            flex={view.layout.pickerSlotFlex}
            minW={0}
            maxW="100%"
            w="full"
            {...invalidOutline("start")}
          >
            <PhaseRangeField
              model={view.start}
              ariaLabel={view.startAriaLabel}
              fieldSx={view.fieldSx.start}
              {...pickerChrome}
            />
          </Box>

          {view.layout.showArrow ? (
            <Text
              fontSize="sm"
              fontWeight="normal"
              color="gray.400"
              flexShrink={0}
              lineHeight="23px"
              letterSpacing="-0.42px"
              alignSelf="center"
              px={{ base: 0.5, sm: 0 }}
            >
              →
            </Text>
          ) : null}

          {view.layout.showEndSlot ? (
            <Box
              flex={view.layout.pickerSlotFlex}
              minW={0}
              maxW="100%"
              w="full"
              {...invalidOutline("end")}
            >
              <PhaseRangeField
                model={view.end}
                ariaLabel={view.endAriaLabel}
                fieldSx={view.fieldSx.end}
                {...pickerChrome}
              />
            </Box>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );
}
